/**
 * Heidi Loves Pets — Shopify OS 2.0 Theme
 * cart-drawer.js — AJAX cart implementation (Custom Element)
 * Vanilla JS only. No jQuery.
 */

/* =============================================================================
   CartDrawer Custom Element
   ============================================================================= */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Internal state
    this._cart         = null;
    this._isOpen       = false;
    this._focusTrigger = null; // element that opened the drawer — focus returns here on close

    // Bind methods to preserve context
    this._onOverlayClick  = this._onOverlayClick.bind(this);
    this._onKeyDown       = this._onKeyDown.bind(this);
  }

  connectedCallback() {
    // Elements inside the drawer
    this._overlay      = document.querySelector('.cart-drawer-overlay');
    this._closeBtn     = this.querySelector('.cart-drawer__close');
    this._itemsEl      = this.querySelector('.cart-drawer__body');
    this._subtotalEl   = this.querySelector('.cart-drawer__subtotal-price');
    this._badgeEls     = document.querySelectorAll('.cart-badge');

    // Wire close button
    if (this._closeBtn) {
      this._closeBtn.addEventListener('click', () => this.close());
    }

    // Wire overlay click
    if (this._overlay) {
      this._overlay.addEventListener('click', this._onOverlayClick);
    }

    // Wire global ESC key
    document.addEventListener('keydown', this._onKeyDown);

    // Listen for open requests from the rest of the page
    document.addEventListener('cart-drawer:open', () => this.open());

    // Wire all add-to-cart buttons that already exist in the DOM
    this._wireAddToCartButtons(document);

    // Also wire any new buttons added dynamically (e.g., section re-renders in Theme Editor)
    document.addEventListener('shopify:section:load', (e) => {
      this._wireAddToCartButtons(e.target);
    });
  }

  disconnectedCallback() {
    if (this._overlay) this._overlay.removeEventListener('click', this._onOverlayClick);
    document.removeEventListener('keydown', this._onKeyDown);
  }

  /* ---------------------------------------------------------------------------
     Open / Close
     --------------------------------------------------------------------------- */
  open(triggerElement) {
    if (this._isOpen) return;

    this._focusTrigger = triggerElement || document.activeElement;
    this._isOpen       = true;

    this.classList.add('open');
    this.setAttribute('aria-hidden', 'false');
    if (this._overlay) this._overlay.classList.add('open');

    document.body.style.overflow = 'hidden';

    // Focus the close button for keyboard/screen-reader users
    if (this._closeBtn) {
      setTimeout(() => this._closeBtn.focus(), 50);
    }

    // Always fetch fresh cart data when opening
    this.fetchCart();
  }

  close() {
    if (!this._isOpen) return;

    this._isOpen = false;

    this.classList.remove('open');
    this.setAttribute('aria-hidden', 'true');
    if (this._overlay) this._overlay.classList.remove('open');

    document.body.style.overflow = '';

    // Return focus to the element that triggered the drawer
    if (this._focusTrigger && typeof this._focusTrigger.focus === 'function') {
      this._focusTrigger.focus();
    }
  }

  /* ---------------------------------------------------------------------------
     Fetch Cart from Shopify
     --------------------------------------------------------------------------- */
  async fetchCart() {
    try {
      this._setLoading(true);
      const res  = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      this._cart = await res.json();
      this._render();
    } catch (err) {
      console.error('[CartDrawer] fetchCart error:', err);
      this._renderError('Unable to load cart. Please try refreshing the page.');
    } finally {
      this._setLoading(false);
    }
  }

  /* ---------------------------------------------------------------------------
     Render Cart Items
     --------------------------------------------------------------------------- */
  _render() {
    if (!this._itemsEl || !this._cart) return;

    const cart = this._cart;

    // Update count badge
    this._updateCartCount(cart.item_count);

    if (!cart.items || cart.items.length === 0) {
      this._renderEmpty();
      return;
    }

    // Build items HTML
    const itemsHTML = cart.items.map(item => this._buildItemHTML(item)).join('');

    this._itemsEl.innerHTML = `<div class="cart-items">${itemsHTML}</div>`;

    // Update subtotal
    if (this._subtotalEl) {
      this._subtotalEl.textContent = this._formatMoney(cart.total_price);
    }

    // Wire up item controls
    this._wireItemControls();
  }

  _buildItemHTML(item) {
    const imgHTML = item.image
      ? `<img src="${item.image}" alt="${this._escapeHtml(item.product_title)}" loading="lazy" width="80" height="80">`
      : `<img src="{{ 'placeholder-product.webp' | asset_url }}" alt="${this._escapeHtml(item.product_title)}" loading="lazy" width="80" height="80">`;

    const variantTitle = (item.variant_title && item.variant_title !== 'Default Title')
      ? `<div class="cart-item__variant">${this._escapeHtml(item.variant_title)}</div>`
      : '';

    return `
      <div class="cart-item" data-line="${item.line_index !== undefined ? item.line_index : ''}" data-key="${item.key}">
        <div class="cart-item__image">
          <a href="${item.url}">${imgHTML}</a>
        </div>
        <div class="cart-item__info">
          <div class="cart-item__title">
            <a href="${item.url}">${this._escapeHtml(item.product_title)}</a>
          </div>
          ${variantTitle}
          <div class="cart-item__price">${this._formatMoney(item.final_line_price)}</div>
          <div class="cart-item__controls">
            <div class="cart-item__qty">
              <button class="cart-item__qty-btn"
                      data-action="decrease"
                      data-key="${item.key}"
                      aria-label="Decrease quantity of ${this._escapeHtml(item.product_title)}">
                &minus;
              </button>
              <span class="cart-item__qty-val" aria-live="polite">${item.quantity}</span>
              <button class="cart-item__qty-btn"
                      data-action="increase"
                      data-key="${item.key}"
                      aria-label="Increase quantity of ${this._escapeHtml(item.product_title)}">
                &plus;
              </button>
            </div>
            <button class="cart-item__remove"
                    data-key="${item.key}"
                    aria-label="Remove ${this._escapeHtml(item.product_title)} from cart">
              Remove
            </button>
          </div>
        </div>
      </div>
    `.trim();
  }

  _renderEmpty() {
    if (this._itemsEl) {
      this._itemsEl.innerHTML = `
        <div class="cart-drawer__empty">
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="btn btn-primary btn-sm">Shop Now</a>
        </div>
      `;
    }
    if (this._subtotalEl) this._subtotalEl.textContent = this._formatMoney(0);
  }

  _renderError(message) {
    if (this._itemsEl) {
      this._itemsEl.innerHTML = `<p style="padding:1.5rem;text-align:center;color:var(--color-text-light);">${this._escapeHtml(message)}</p>`;
    }
  }

  /* ---------------------------------------------------------------------------
     Wire Item Controls (quantity buttons + remove)
     --------------------------------------------------------------------------- */
  _wireItemControls() {
    if (!this._itemsEl) return;

    // Quantity buttons
    this._itemsEl.querySelectorAll('.cart-item__qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key       = btn.dataset.key;
        const action    = btn.dataset.action;
        const item      = this._cart && this._cart.items.find(i => i.key === key);
        if (!item) return;

        const newQty = action === 'decrease'
          ? Math.max(0, item.quantity - 1)
          : item.quantity + 1;

        this.updateQuantity(key, newQty);
      });
    });

    // Remove buttons
    this._itemsEl.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        this.removeItem(key);
      });
    });
  }

  /* ---------------------------------------------------------------------------
     AJAX Cart Mutations
     --------------------------------------------------------------------------- */

  /**
   * Update the quantity of a line item.
   * Uses /cart/change.js with `id` (variant key) and `quantity`.
   */
  async updateQuantity(key, quantity) {
    try {
      this._setLoading(true);
      const res = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id: key, quantity }),
      });

      if (!res.ok) throw new Error('Cart update failed: ' + res.status);
      this._cart = await res.json();
      this._render();
      this._dispatchCartUpdated(this._cart.item_count);
    } catch (err) {
      console.error('[CartDrawer] updateQuantity error:', err);
      if (window.__hlp && window.__hlp.showToast) {
        window.__hlp.showToast('Could not update cart. Please try again.', 'error');
      }
    } finally {
      this._setLoading(false);
    }
  }

  /**
   * Remove a line item completely.
   */
  async removeItem(key) {
    await this.updateQuantity(key, 0);
  }

  /**
   * Add a variant to cart (called by AddToCartButton handler below).
   * Returns the updated cart or throws on error.
   */
  async addItem(variantId, quantity, properties) {
    quantity   = quantity   || 1;
    properties = properties || {};

    const body = { items: [{ id: variantId, quantity, properties }] };

    const res = await fetch('/cart/add.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });

    if (!res.ok) {
      let errMsg = 'Could not add to cart.';
      try {
        const errData = await res.json();
        errMsg = errData.description || errData.message || errMsg;
      } catch (_) { /* ignore parse error */ }
      throw new Error(errMsg);
    }

    // Fetch full cart after add (add.js only returns added items)
    const cartRes  = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    this._cart     = await cartRes.json();
    this._dispatchCartUpdated(this._cart.item_count);
    return this._cart;
  }

  /* ---------------------------------------------------------------------------
     Wire Add-to-Cart Buttons
     --------------------------------------------------------------------------- */
  _wireAddToCartButtons(root) {
    const buttons = root.querySelectorAll('.btn-add-cart, [data-add-to-cart]');
    buttons.forEach(btn => {
      // Avoid double-binding
      if (btn.dataset.cartBound === 'true') return;
      btn.dataset.cartBound = 'true';

      btn.addEventListener('click', async (e) => {
        e.preventDefault();

        const variantId = parseInt(btn.dataset.variantId, 10);
        if (!variantId) {
          console.warn('[CartDrawer] Missing data-variant-id on button', btn);
          return;
        }

        const quantity   = parseInt(btn.dataset.quantity, 10) || 1;
        const originalText = btn.textContent.trim();

        btn.disabled     = true;
        btn.textContent  = 'Adding…';

        try {
          await this.addItem(variantId, quantity);
          btn.textContent = 'Added!';

          // Open drawer
          this.open(btn);

          // Render updated cart (already fetched in addItem)
          this._render();

          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled    = false;
          }, 2000);
        } catch (err) {
          console.error('[CartDrawer] addItem error:', err);
          btn.textContent = 'Unavailable';
          btn.disabled    = true;

          if (window.__hlp && window.__hlp.showToast) {
            window.__hlp.showToast(err.message || 'Could not add to cart.', 'error');
          }

          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled    = false;
          }, 3000);
        }
      });
    });
  }

  /* ---------------------------------------------------------------------------
     Helpers
     --------------------------------------------------------------------------- */
  _updateCartCount(count) {
    this._dispatchCartUpdated(count);
  }

  _dispatchCartUpdated(count) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail:  { count: count || 0 },
    }));
  }

  _setLoading(isLoading) {
    if (!this._itemsEl) return;
    if (isLoading) {
      this._itemsEl.setAttribute('aria-busy', 'true');
      // Only show spinner if items area is otherwise empty
      if (!this._itemsEl.querySelector('.cart-items')) {
        this._itemsEl.innerHTML = `
          <div class="cart-drawer__loading" aria-live="polite">
            <div class="spinner" role="status"><span class="sr-only">Loading cart…</span></div>
          </div>
        `;
      }
    } else {
      this._itemsEl.removeAttribute('aria-busy');
    }
  }

  _formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    const amount = (cents / 100).toFixed(2);
    const currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active)
      ? Shopify.currency.active
      : 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  }

  _escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g,  '&amp;')
      .replace(/</g,  '&lt;')
      .replace(/>/g,  '&gt;')
      .replace(/"/g,  '&quot;')
      .replace(/'/g,  '&#039;');
  }

  _onOverlayClick() {
    this.close();
  }

  _onKeyDown(e) {
    if (e.key === 'Escape' && this._isOpen) {
      this.close();
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
